// =====================================================================
// src/lib/hospedes.js — o que a tela de Hóspedes decide, sem DOM
//
// Puro. Quem cadastra onde, a capacidade e o titular único são do banco
// (0016); aqui: agrupar reservas com a lotação, validar o formulário antes
// da rede, formatar CPF/idade, e escolher o que a busca de repetidos
// preenche.
// =====================================================================
import { fase, noites, dataCurta } from './reservas.js';
import { BRASIL, cepValido, nomeDoPais, formatarCep } from './ibge.js';
// 0090: o CPF da ficha passa pela MESMA conferência de dígitos do login
// (lib/usuarios.js) — uma régua só, e a do banco é `app.cpf_valido`
import { cpfValido } from './usuarios.js';

const semAcento = s => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const chaveQuarto = n => { const m = /^(\d+)(.*)$/.exec(String(n || '')); return m ? m[1].padStart(6, '0') + m[2].toLowerCase() : 'z' + String(n || '').toLowerCase(); };

export const digitos = s => String(s || '').replace(/\D/g, '');

// 12345678901 → 123.456.789-01; passaporte como veio.
export function formatarDocumento(doc, tipo) {
  if (!doc) return '';
  const d = digitos(doc);
  if ((tipo === 'CPF' || (!tipo && d.length === 11 && /^[\d.\-\s]+$/.test(doc))) && d.length === 11) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  return String(doc);
}

export function idade(nascimentoISO, hojeISO) {
  if (!nascimentoISO) return null;
  const [a, m, d] = nascimentoISO.split('-').map(Number); const [ha, hm, hd] = hojeISO.split('-').map(Number);
  if (!a || !ha) return null;
  let i = ha - a; if (hm < m || (hm === m && hd < d)) i--;
  return i < 0 ? null : i;
}

// Uma reserva com a sua gente: lotação "2/3", quem é o titular, se está
// cheia, e a fase (chega hoje, hospedada…). Só confirmadas interessam aqui.
export function reservasComGente(reservas, hoje) {
  return (reservas || [])
    .filter(r => r.situacao === 'confirmada')
    .map(r => {
      const gente = (r.hospedes || []).slice().sort((a, b) => (b.principal - a.principal) || String(a.nome).localeCompare(String(b.nome)));
      const cap = r.unidades ? Number(r.unidades.capacidade) || 0 : 0;
      return { ...r, quarto: r.unidades ? r.unidades.numero : '', capacidade: cap, gente, lotacao: `${gente.length}/${cap || '?'}`, cheia: cap > 0 && gente.length >= cap,
        semGente: gente.length === 0, titular: (gente.find(h => h.principal) || gente[0] || null), fase: fase(r, hoje) };
    })
    .sort((a, b) => a.check_in.localeCompare(b.check_in) || chaveQuarto(a.quarto).localeCompare(chaveQuarto(b.quarto)));
}

export function filtrarReservasComGente(lista, texto) {
  const t = semAcento(texto).trim();
  if (!t) return lista;
  return (lista || []).filter(r => semAcento(r.quarto).includes(t) || r.gente.some(h => semAcento(h.nome).includes(t) || digitos(h.documento).includes(digitos(t) || '§')));
}

export const TIPOS_DOCUMENTO = Object.freeze([['CPF', 'CPF'], ['PASSAPORTE', 'Passaporte']]);

// REGRA PERMANENTE do Hugo, 19/09/2026:
//
//   "sempre utilize nome e sobrenome na identificacao de hospedes em
//    qualquer situacao"
//
// Por isso a exigência mora AQUI, e não em cada tela. `validarHospede` é a
// validação compartilhada — o módulo de Hóspedes, o quadro do balcão e o
// link do hóspede chamam esta mesma função. Escrever a regra em cada uma
// delas seria três lugares para a regra divergir, e a quarta tela que
// nascesse amanhã não teria nenhum.
export function validarHospede({ nome, sobrenome, documento, tipo_documento, data_nascimento, email }, hoje) {
  const erros = [];
  if (!String(nome || '').trim()) erros.push('Nome é obrigatório');
  if (!String(sobrenome || '').trim()) erros.push('Informe o sobrenome');
  const d = String(documento || '').trim();
  if (d && tipo_documento === 'CPF' && digitos(d).length !== 11) erros.push('CPF tem 11 dígitos');
  else if (d && tipo_documento === 'CPF' && !cpfValido(d)) erros.push('CPF inválido — confira os dígitos');   // 0090, a mensagem do banco
  if (d && tipo_documento && !['CPF', 'PASSAPORTE'].includes(tipo_documento)) erros.push('Tipo de documento é CPF ou Passaporte');
  if (data_nascimento && hoje && data_nascimento > hoje) erros.push('Data de nascimento no futuro');
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim())) erros.push('E-mail inválido');
  return erros;
}

// ── JUNTAR E SEPARAR, SEM CHUTE ──────────────────────────────────────
//
// `nomeCompleto` é o espelho de `app.nome_completo` (0041, linha 45): a
// MESMA regra dos dois lados, porque a tela precisa mostrar antes de
// gravar o que o banco vai gravar. Se um dia divergirem, quem manda é o
// banco — esta aqui existe só para a pessoa ver o resultado antes de
// clicar em Salvar.
export const nomeCompleto = (nome, sobrenome) =>
  [String(nome == null ? '' : nome).trim(), String(sobrenome == null ? '' : sobrenome).trim()]
    .filter(Boolean).join(' ').replace(/\s+/g, ' ');

// `separarNome` faz o caminho de volta, para PREENCHER os dois campos a
// partir de uma linha já gravada — e o ponto dela é o que ela NÃO faz.
//
// A coluna `nome` guarda o nome completo em toda linha, nova ou velha
// (0041). Quando `sobrenome` está preenchido, o corte é exato: o CHECK
// `hospedes_sobrenome_fecha_o_nome` garante que o sobrenome é literalmente
// o fim do nome, então tirar o fim devolve o primeiro nome sem adivinhar
// nada.
//
// Quando `sobrenome` é NULO — as fichas anteriores a 19/09/2026 —, o nome
// completo vai inteiro para o campo Nome e o campo Sobrenome fica vazio.
// Partir "Maria de Lourdes Silva" no último espaço seria exatamente a
// gambiarra no chute que o Hugo proibiu: o palpite viraria dado gravado,
// com cara de dado conferido. Quem sabe onde parte é a pessoa no balcão,
// e é ela que completa — vendo a prévia do nome completo enquanto digita.
export function separarNome(h) {
  const nome = String((h || {}).nome == null ? '' : h.nome).trim();
  const sobre = String((h || {}).sobrenome == null ? '' : h.sobrenome).trim();
  if (!sobre) return { nome, sobrenome: '' };
  const corte = nome.length - sobre.length;
  // O `endsWith` não é decoração: linha antiga corrigida a mão pode ter os
  // dois divergindo, e aí devolver `nome` inteiro é a resposta honesta.
  return corte > 0 && nome.slice(corte) === sobre
    ? { nome: nome.slice(0, corte).trim(), sobrenome: sobre }
    : { nome, sobrenome: sobre };
}

// Tipo deduzido do documento, para o formulário pré-escolher.
export function tipoSugerido(documento) {
  const d = String(documento || '').trim();
  if (!d) return '';
  return /^[\d.\-\s]+$/.test(d) && digitos(d).length === 11 ? 'CPF' : 'PASSAPORTE';
}

// O tipo enquanto a pessoa DIGITA (simulação de 29/10/2026, recepcao-8).
// Antes, a tela sugeria o tipo só quando o campo estava vazio: na PRIMEIRA
// tecla o documento tinha um dígito, a sugestão era PASSAPORTE, e o tipo
// travava aí — o CPF digitado tecla a tecla era gravado como passaporte (e
// ia assim para a FNRH). Colando o número inteiro saía CPF, por isso o erro
// não aparecia em teste rápido. Agora a sugestão acompanha cada tecla até a
// pessoa ESCOLHER o tipo na lista; dali em diante, vale a escolha dela.
export function tipoAoDigitar(documento, { escolhidoAMao = false, atual = '' } = {}) {
  return escolhidoAMao ? atual : tipoSugerido(documento);
}

// O que uma pessoa achada na busca de repetidos preenche no formulário.
// 0042: `hospedes_repetidos` passou a devolver `sobrenome`, e por isso os
// dois campos voltam separados — `separarNome` desfaz a junção que o banco
// fez. Sem a coluna nova (hóspede antigo, sobrenome nulo), o nome completo
// fica no primeiro campo e o segundo vem vazio: a pessoa completa, ninguém
// chuta.
export function preencherDeRepetido(p) {
  const n = separarNome(p);
  return { nome: n.nome, sobrenome: n.sobrenome, documento: formatarDocumento(p.documento, p.tipo_documento), tipo_documento: p.tipo_documento || tipoSugerido(p.documento),
    data_nascimento: p.data_nascimento || '', telefone: p.telefone || '', email: p.email || '' };
}

// "2 estadias · última em 12/08" — o rótulo da sugestão.
export function rotuloRepetido(p, hoje) {
  const n = Number(p.estadias) || 0;
  return `${n} estadia${n === 1 ? '' : 's'} · última ${dataCurta(p.ultima_estadia, hoje)}${p.documento ? ' · ' + formatarDocumento(p.documento, p.tipo_documento) : ''}`;
}

export function acoesDoHospede(h, { podeEditar }) {
  if (!podeEditar) return [];
  const a = ['editar'];
  if (!h.principal) a.push('titular');
  if (!h.chegada_em) a.push('remover');
  return a;
}

// ── 0025: a ficha completa (FNRH) dentro do modal de hóspede ─────────
// As onze colunas que a Portaria MTur 177/2011 pede. Moram AQUI, e não em
// precheckin.js, porque são colunas de `hospedes`: a página do hóspede é
// só mais um lugar que as preenche. precheckin.js reexporta.
//
// 0062 (módulo FNRH, 25/09/2026): mais quatro de texto — nome social, sexo,
// bairro, complemento — e o "legalmente incapaz", que é sim/não e por isso
// fica FORA desta lista (ele não é "preenchido ou vazio": falso é resposta).
// 0070 (API da FNRH, 29/09/2026): país em código (tabela do IBGE),
// município em código IBGE para quem mora no Brasil, a cidade digitada só
// para quem mora fora, e o endereço partido em logradouro + número. Saem
// `nacionalidade`, `pais` e `endereco` — viraram TEXTO ANTIGO (ver
// `textoAntigo` abaixo) — e a `uf`, que é a do município (ela continua no
// formulário, mas só para filtrar a lista de municípios: não se envia).
export const CAMPOS_FNRH = Object.freeze(['nacionalidade_pais', 'residencia_pais', 'municipio_id', 'cidade', 'cep', 'logradouro', 'numero_endereco',
  'profissao', 'procedencia', 'destino', 'motivo_viagem', 'meio_transporte',
  'nome_social', 'sexo', 'bairro', 'complemento',
  // 0064: raça/cor — só com o módulo FNRH contratado; sem ele o banco
  // recusa (a tela nem mostra o campo)
  'raca',
  // 0065: a descrição do gênero "Outro" (qualquer hotel, como o gênero) e a
  // deficiência com o tipo (saúde: a mesma trava da raça)
  'genero_descricao', 'deficiencia', 'tipo_deficiencia']);

// O que a ficha precisa ter para contar como COMPLETA. A base vale para
// todos; o resto depende de onde a pessoa mora (0070), como na API:
//   · no Brasil: município, CEP e bairro (a API pede bairro com CEP);
//   · fora: a cidade (a API não tem o campo; a Portaria pede o município).
// Nome social e complemento não entram — nem todo hóspede tem.
export const CAMPOS_FNRH_OBRIGATORIOS = Object.freeze(['nacionalidade_pais', 'residencia_pais', 'profissao', 'logradouro', 'numero_endereco',
  'procedencia', 'destino', 'motivo_viagem', 'meio_transporte', 'sexo']);
export const CAMPOS_MORA_NO_BRASIL = Object.freeze(['municipio_id', 'cep', 'bairro']);
export const CAMPOS_MORA_FORA = Object.freeze(['cidade']);

// 0065 (Hugo, 26/09/2026): onde o hotel COLETA — o módulo FNRH contratado,
// `coleta_raca` —, a ficha só fecha com o que a API da FNRH exige:
// documento de todos (menor inclusive: "faltar documento = ficha
// incompleta"), raça/cor e deficiência ("Prefiro não informar" é resposta).
// Sem o módulo, a régua continua a de antes: o hotel nem pergunta.
export const CAMPOS_FNRH_COLETA = Object.freeze(['documento', 'raca', 'deficiencia']);

// A régua de UMA pessoa: a base, a da coleta, e a descrição quando o gênero
// é "Outro" (a API exige GeneroDescricao; vale para todo hotel, porque o
// gênero todo hotel pergunta).
export function camposObrigatorios(h, coleta = false) {
  const res = String((h || {}).residencia_pais || '');
  const c = [...CAMPOS_FNRH_OBRIGATORIOS,
    ...(res === BRASIL ? CAMPOS_MORA_NO_BRASIL : res ? CAMPOS_MORA_FORA : []),
    ...(coleta ? CAMPOS_FNRH_COLETA : [])];
  if (String((h || {}).sexo || '') === 'OUTRO') c.splice(c.indexOf('sexo') + 1, 0, 'genero_descricao');
  return c;
}

// O LEGADO (0065): chaves antigas que a migration NÃO converteu, porque a
// conversão seria chute — na 0062 "carro" era "Veículo próprio" (quem veio
// de moto pode ter marcado) e "onibus" era "Rodoviário"; "evento" não é
// necessariamente congresso; "outro" não tem destino. Ficam na coluna,
// nenhuma tela oferece, e contam como FALTANDO.
export const LEGADO = Object.freeze({
  meio_transporte: Object.freeze({ onibus: 'Rodoviário', carro: 'Veículo próprio', outro: 'Outro' }),
  motivo_viagem: Object.freeze({ evento: 'Evento / congresso', outro: 'Outro' }),
});
export const ehLegado = (campo, v) => !!(LEGADO[campo] && Object.prototype.hasOwnProperty.call(LEGADO[campo], String(v == null ? '' : v)));
// 60 é o limite NOSSO (a CHECK da 0065): a API não publica um.
export const MAX_GENERO_DESCRICAO = 60;

// O par vai coerente ou não vai (0065): a descrição só com "Outro", o tipo
// só com "Sim". Uma linha escondida na tela pode guardar o valor de antes
// da troca — é aqui que ele cai, e não em cada tela.
export function fichaParaSalvar(f) {
  const x = { ...(f || {}) };
  if (x.sexo !== 'OUTRO') x.genero_descricao = '';
  if (x.deficiencia !== 'SIM') x.tipo_deficiencia = '';
  // 0070: município só de quem mora no Brasil; cidade digitada só de quem mora fora
  if (x.residencia_pais === BRASIL) x.cidade = ''; else x.municipio_id = '';
  if (!x.residencia_pais) x.cidade = '';
  return x;
}

// Os erros da ficha que o banco também recusa — aqui para aparecerem ANTES
// de gravar, em qualquer tela (Hóspedes, balcão, link).
export function errosDaFicha(f) {
  const x = f || {}; const e = [];
  const desc = String(x.genero_descricao || '').trim();
  if (x.sexo === 'OUTRO' && !desc) e.push('Gênero "Outro": descreva o gênero');
  if (desc.length > MAX_GENERO_DESCRICAO) e.push(`A descrição do gênero tem até ${MAX_GENERO_DESCRICAO} caracteres`);
  if (x.deficiencia === 'SIM' && !String(x.tipo_deficiencia || '').trim()) e.push('Pessoa com deficiência: escolha o tipo');
  // 0070: o CEP brasileiro, pela regra do banco
  if (x.residencia_pais === BRASIL && String(x.cep || '').trim() && !cepValido(x.cep)) e.push('CEP tem 8 dígitos');
  return e;
}

// ── 0070: o TEXTO ANTIGO ──────────────────────────────────────────────
// O que foi digitado antes das listas do IBGE e não converteu (a 0070 só
// converte o que bate). A ficha mostra ao lado do campo novo — "antes:
// Americana" — para a recepção perguntar sabendo o que estava escrito.
// Cada um só aparece enquanto o campo que o substitui está vazio (é
// assim que o banco o apaga).
export function textoAntigo(h) {
  const x = h || {}; const t = v => String(v == null ? '' : v).trim();
  const r = {};
  if (t(x.nacionalidade) && !t(x.nacionalidade_pais)) r.nacionalidade_pais = t(x.nacionalidade);
  if (t(x.pais) && !t(x.residencia_pais)) r.residencia_pais = t(x.pais);
  if (t(x.endereco) && !t(x.logradouro)) r.logradouro = t(x.endereco);
  // a cidade de quem mora no Brasil (ou não disse onde) sem município é texto antigo
  if (t(x.cidade) && !t(x.municipio_id) && (!t(x.residencia_pais) || t(x.residencia_pais) === BRASIL)) r.municipio_id = [t(x.cidade), t(x.uf)].filter(Boolean).join('/');
  return r;
}

// O endereço numa linha, para a lista: "Rua A, 10 · 74000-000". Sem
// logradouro, o texto antigo (0070).
export function linhaDeEndereco(h) {
  const x = h || {}; const t = v => String(v == null ? '' : v).trim();
  const rua = [t(x.logradouro), t(x.numero_endereco)].filter(Boolean).join(', ') || t(x.endereco);
  const cep = t(x.residencia_pais) === BRASIL ? formatarCep(x.cep) : t(x.cep);
  return [rua, cep].filter(Boolean).join(' · ');
}

// Onde a pessoa mora, numa linha, para listas e resumo: "Curitiba/PR",
// "Lisboa, Portugal". `municipioNome` vem do banco (ficha_da_reserva,
// fnrh_do_periodo) ou do índice do IBGE; sem ele, o texto antigo.
export function residencia(h, municipioNome = '') {
  const x = h || {}; const t = v => String(v == null ? '' : v).trim();
  const res = t(x.residencia_pais);
  if (res === BRASIL) return t(municipioNome || x.municipio) ? `${t(municipioNome || x.municipio)}/${t(x.uf)}` : [t(x.cidade), t(x.uf)].filter(Boolean).join('/');
  if (res) return [t(x.cidade), nomeDoPais(res)].filter(Boolean).join(', ');
  return [[t(x.cidade), t(x.uf)].filter(Boolean).join('/'), t(x.pais)].filter(Boolean).join(', ');
}

// `uf` vai junto no formulário (filtra a lista de municípios), fora da lista que se envia
export const fichaVazia = () => ({ ...Object.fromEntries(CAMPOS_FNRH.map(c => [c, ''])), uf: '', legalmente_incapaz: false });

// A ficha já tem alguma coisa? Decide o selo do bloco ("veio do
// pré-check-in" × "em branco") e se ele nasce aberto.
export function temFicha(h) {
  return CAMPOS_FNRH.some(c => String((h || {})[c] == null ? '' : h[c]).trim() !== '') || !!(h || {}).legalmente_incapaz
    || Object.keys(textoAntigo(h)).length > 0;   // 0070: ficha só com texto antigo também é ficha
}

export function fichaDoHospede(h) {
  const f = fichaVazia();
  for (const c of CAMPOS_FNRH) f[c] = String((h || {})[c] == null ? '' : h[c]);
  // 0070: a UF do formulário é a do município; sem município, a de antes
  // (texto antigo) ainda ajuda a abrir a lista no estado certo
  f.uf = String((h || {}).uf == null ? '' : h.uf);
  if (f.residencia_pais !== BRASIL && f.residencia_pais) f.uf = '';
  // a cidade de quem mora no Brasil é texto antigo: não entra no campo
  if (f.residencia_pais !== '' && f.residencia_pais === BRASIL) f.cidade = '';
  if (!f.residencia_pais) f.cidade = '';
  // 0062/0065: o LEGADO não está na lista — a tela abre VAZIO para a
  // recepção escolher, em vez de mostrar uma opção que não existe (e que o
  // banco não aceita mais)
  for (const c of Object.keys(LEGADO)) if (ehLegado(c, f[c])) f[c] = '';
  f.legalmente_incapaz = !!(h || {}).legalmente_incapaz;
  return f;
}

// ── DUAS FORMAS DA MESMA PESSOA, E O DEFEITO QUE ELAS CRIARAM ────────
//
// DEFEITO ACHADO EM 20/09/2026 pela prova de tela, e ele era SILENCIOSO:
// clicar em "Tornar titular" renomeava o hóspede.
//
// `argumentosDoHospede` recebe `dados` em duas formas diferentes, e até
// aqui nada dizia isso em lugar nenhum:
//
//   FORMA DO FORMULÁRIO   nome = "Marina"        sobrenome = "Costa"
//   FORMA DA LINHA        nome = "Marina Costa"  sobrenome = "Costa"
//
// A linha do banco guarda o nome COMPLETO (0041). Mandar a linha crua
// para `salvar_hospede` faz o banco juntar de novo — `app.nome_completo`
// CONCATENA, sem perguntar — e grava "Marina Costa Costa". Pior: o CHECK
// `hospedes_sobrenome_fecha_o_nome` continua satisfeito, porque o nome
// dobrado ainda termina no sobrenome. Nenhuma trava pegaria; só a tela da
// recepcionista, no dia seguinte, com o nome errado na ficha.
//
// `tornarTitular` fazia exatamente isso (`dados: h`), e é uma ação de UM
// clique, sem formulário e sem confirmação.
//
// A correção NÃO é `argumentosDoHospede` adivinhar a forma: "João Silva"
// + "Silva" é forma de formulário de um homem que se chama João Silva
// Silva, e é forma de linha de um João Silva. Quem sabe qual é quem é
// quem chama. Então a conversão é explícita, e é esta função.
export const dadosDaLinha = h => ({ ...(h || {}), ...separarNome(h) });

// O corpo da chamada de salvar_hospede (0025).
//
// `dados` vem na FORMA DO FORMULÁRIO: `nome` é só o primeiro nome, porque
// quem junta é o banco. Quem tem uma linha do banco na mão passa por
// `dadosDaLinha(h)` antes — ver o bloco acima.
//
// `editarFnrh` é o consentimento explícito de mexer na ficha: é o
// `p_editar_fnrh` da migration. Com ele FALSO, o banco preserva as onze
// colunas — quer dizer, salvar o modal com o bloco fechado não apaga o que
// o hóspede preencheu no celular. É por isso que os campos da ficha nem
// sequer são enviados nesse caso: mandar `null` com o bloco fechado seria
// exatamente o apagão que a 0025 existe para impedir.
export function argumentosDoHospede({ id = null, reserva_id, dados, ficha = null, principal = false, editarFnrh = false }) {
  const t = v => { const s = String(v == null ? '' : v).trim(); return s || null; };
  const arg = {
    p_id: id || null, p_reserva_id: reserva_id, p_nome: dados.nome,
    // 0041: `nome` e `sobrenome` vao SEPARADOS; quem junta e o banco
    // (`app.nome_completo`). Mandar tambem o composto seria a mesma verdade
    // em dois lugares, com duas chances de divergir.
    p_sobrenome: t(dados.sobrenome),
    p_documento: t(dados.documento), p_tipo_documento: t(dados.tipo_documento),
    p_data_nascimento: t(dados.data_nascimento), p_telefone: t(dados.telefone), p_email: t(dados.email),
    p_principal: !!principal, p_editar_fnrh: !!editarFnrh,
  };
  if (!arg.p_editar_fnrh) return arg;
  for (const c of CAMPOS_FNRH) arg['p_' + c] = t((ficha || {})[c]);
  arg.p_legalmente_incapaz = !!(ficha || {}).legalmente_incapaz;
  return arg;
}

// ── 16/09/2026: os três cards do topo, no desenho do Monteiro de Moraes ──
//
// Lá são três cards de plataforma (Airbnb, Booking, Direto), cada um com
// dois números grandes: `cadastrados` e `pendentes`. Plataforma não existe
// no hotel — e o Hugo escolheu, em 16/09, o eixo que a recepção usa de
// manhã: HOSPEDADOS AGORA · CHEGAM HOJE · SAEM HOJE. São as mesmas três
// fases que `FASES` já nomeia em reservas.js, com as mesmas cores das
// badges (verde, azul, amarelo) — nada inventado.
//
// O segundo número, o `pendentes` dele, é aqui a FICHA FNRH INCOMPLETA:
// falta pelo menos um dos onze campos da Portaria MTur 177/2011. Também
// decisão do Hugo, e é a que o hotel vai ser cobrado.
//
// O que NÃO veio de lá, e por quê:
//   - o "faturado" em letra miúda embaixo do nome. INVARIANTE 2: nenhum
//     perfil do hotel vê valor de reserva.
//   - o botão de WhatsApp da trinca. Disparo de mensagem é fase 3 da
//     proposta; não existe módulo.
//   - "Aniversariantes do mês". Sem módulo de mensagem não há o que fazer
//     com a lista além de olhar. Decisão do Hugo: fica para a fase 3.

export const FAIXAS_HOSPEDES = Object.freeze([
  { chave: 'hospedada', rotulo: 'Hospedados agora', icone: 'ic-hospedes', cor: '#059669', fundo: '#ecfdf5' },
  { chave: 'chega', rotulo: 'Chegam hoje', icone: 'ic-checkin', cor: '#1B3A7A', fundo: '#eff6ff' },
  { chave: 'sai', rotulo: 'Saem hoje', icone: 'ic-door', cor: '#b45309', fundo: '#fffbeb' },
]);

// Quais dos campos obrigatórios da FNRH faltam nesta pessoa. O LEGADO
// (0062/0065) conta como faltando: não é mais resposta. `coleta` é o
// `coleta_raca` do hotel (módulo FNRH contratado) — muda a régua.
export function faltaNaFicha(h, coleta = false) {
  return camposObrigatorios(h, coleta).filter(c => {
    const v = String((h || {})[c] == null ? '' : h[c]).trim();
    return v === '' || ehLegado(c, v);
  });
}
export const fichaCompleta = (h, coleta = false) => faltaNaFicha(h, coleta).length === 0;

// Os três cards, já contados. Entra o que `reservasComGente` devolve (que
// já traz `fase` e `gente`); sai um por faixa, na ordem de FAIXAS_HOSPEDES.
//
// `pessoas` conta GENTE, não reserva: o card diz quantos hóspedes, porque
// é ficha de hóspede que falta preencher. Uma reserva sem ninguém
// cadastrado não soma pessoa nenhuma — e é justamente por isso que ela
// aparece na lista de baixo com "Ninguém cadastrado ainda".
export function resumoDeHospedes(comGente, coleta = false) {
  return FAIXAS_HOSPEDES.map(f => {
    const reservas = (comGente || []).filter(r => r.fase === f.chave);
    const gente = reservas.flatMap(r => r.gente || []);
    return {
      ...f,
      reservas: reservas.length,
      pessoas: gente.length,
      pendentes: gente.filter(h => !fichaCompleta(h, coleta)).length,
    };
  });
}

// As linhas do PDF e da planilha de Hóspedes. As mesmas colunas nos dois —
// quem manda os dois juntos não deve achar que são coisas diferentes.
//
// Sem valor de reserva: INVARIANTE 2.
//
// O documento vai INTEIRO, formatado igual ao da tela. É de propósito e
// não é descuido: o arquivo existe para a ficha da Portaria MTur, que pede
// o documento, e quem baixa já está vendo o mesmo número na tela ao lado.
// Um CPF mascarado num PDF de FNRH não serve para nada.
export const COLUNAS_HOSPEDES = Object.freeze(['Quarto', 'Período', 'Hóspede', 'Titular', 'Documento', 'Nascimento', 'Telefone', 'E-mail', 'Ficha FNRH']);

export function linhasDeHospedes(comGente, hoje, coleta = false) {
  const saida = [];
  for (const r of comGente || []) {
    for (const h of r.gente || []) {
      const falta = faltaNaFicha(h, coleta);
      saida.push([
        r.quarto,
        `${dataCurta(r.check_in)} a ${dataCurta(r.check_out)}`,
        h.nome,
        h.principal ? 'sim' : '',
        h.documento ? `${h.tipo_documento || 'Doc'} ${formatarDocumento(h.documento, h.tipo_documento)}` : '',
        h.data_nascimento ? dataCurta(h.data_nascimento) : '',
        h.telefone || '',
        h.email || '',
        falta.length ? `faltam ${falta.length}` : 'completa',
      ]);
    }
  }
  return saida;
}

export { noites, dataCurta };
